import React, { useState, useEffect } from 'react';
import { SAPLayout } from '@/layouts/SAPLayout/SAPLayout';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';
import { SapMetricsSummary, SapSimulationScenario, SapIntegrationLogItem } from '@/types/domain/sap-simulator';
import { SapDashboardMetricsHeader } from '@/components/sap/SapDashboardMetricsHeader';
import { SapFailureInjectionPanel } from '@/components/sap/SapFailureInjectionPanel';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Activity, ArrowRight, Code } from 'lucide-react';
import { SapPayloadInspectorModal } from '@/components/sap/SapPayloadInspectorModal';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';

export const SapDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<SapMetricsSummary | null>(null);
  const [logs, setLogs] = useState<SapIntegrationLogItem[]>([]);
  const [config, setConfig] = useState(mockSapSimulatorService.getSimulationConfig());
  const [selectedLog, setSelectedLog] = useState<SapIntegrationLogItem | null>(null);

  const loadData = async () => {
    const [m, l] = await Promise.all([
      mockSapSimulatorService.getMetrics(),
      mockSapSimulatorService.getIntegrationLogs(),
    ]);
    setMetrics(m);
    setLogs(l);
    setConfig(mockSapSimulatorService.getSimulationConfig());
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleScenarioChange = (scenario: SapSimulationScenario) => {
    mockSapSimulatorService.setSimulationScenario(scenario);
    setConfig(mockSapSimulatorService.getSimulationConfig());
  };

  return (
    <SAPLayout>
      <div className="space-y-6 font-heading">
        {/* KPI Metrics Header */}
        {metrics && <SapDashboardMetricsHeader metrics={metrics} />}

        {/* Developer Failure Injection Panel */}
        <SapFailureInjectionPanel
          activeScenario={config.activeScenario}
          onScenarioChange={handleScenarioChange}
        />

        {/* Integration Activity & Recent Logs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-border rounded-2xl shadow-2xs">
            <CardHeader className="pb-3 border-b border-border/60 flex items-center justify-between">
              <CardTitle className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
                <Activity className="h-4 w-4 text-primary" />
                Recent Integration Logs & BAPI Calls
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(ROUTES.SAP.LOGS)}
                className="text-xs font-bold rounded-xl"
              >
                View Full Audit Logs <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              {logs.length === 0 ? (
                <div className="p-8 text-center text-xs text-foreground-muted">No integration events recorded</div>
              ) : (
                logs.slice(0, 5).map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-border/80 bg-card hover:border-primary/40 transition-all text-xs"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-primary">{log.apiName}</span>
                        <span className="text-[10px] font-bold text-foreground-muted px-1.5 py-0.5 rounded bg-muted">
                          {log.direction}
                        </span>
                        {log.sapRef && (
                          <span className="text-[10px] font-mono font-bold text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                            SAP Ref: {log.sapRef}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-foreground-secondary font-mono">
                        Ref: {log.requestRef} • Corr ID: {log.correlationId} • {new Date(log.timestamp).toLocaleTimeString()}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <span
                        className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                          log.status === 'SUCCESS'
                            ? 'bg-emerald-500/10 text-emerald-700'
                            : 'bg-rose-500/10 text-rose-700'
                        }`}
                      >
                        {log.status}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedLog(log)}
                        className="h-7 text-[10px] font-bold rounded-lg"
                      >
                        <Code className="h-3 w-3 mr-1" /> Inspect
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Master Reference Status Card */}
          <div className="space-y-4">
            <Card className="border-border rounded-2xl shadow-2xs">
              <CardHeader className="pb-3 border-b border-border/60">
                <CardTitle className="font-heading text-xs font-bold text-foreground uppercase">
                  SAP Environment Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3.5 space-y-2.5 text-xs">
                <div className="flex justify-between py-1 border-b border-border/40 font-medium">
                  <span className="text-foreground-secondary">Integration Mode:</span>
                  <strong className="text-primary font-mono font-bold">SIMULATOR_DEVELOPMENT</strong>
                </div>
                <div className="flex justify-between py-1 border-b border-border/40 font-medium">
                  <span className="text-foreground-secondary">SAP_INTEGRATION_ENABLED:</span>
                  <span className="font-mono text-amber-600 bg-amber-500/10 px-1.5 py-0.5 rounded font-bold">
                    false (Simulated)
                  </span>
                </div>
                <div className="flex justify-between py-1 border-b border-border/40 font-medium">
                  <span className="text-foreground-secondary">Actual SAP ERP:</span>
                  <span className="text-foreground font-semibold">Disconnected (Mock Boundary)</span>
                </div>
                <div className="flex justify-between py-1 font-medium">
                  <span className="text-foreground-secondary">Idempotency Protection:</span>
                  <span className="text-emerald-600 font-bold">ACTIVE (REQ Key Locking)</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Payload Inspector Modal */}
        <SapPayloadInspectorModal
          isOpen={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          logItem={selectedLog}
        />
      </div>
    </SAPLayout>
  );
};
